import _extends from "@babel/runtime/helpers/extends";
import _classCallCheck from "@babel/runtime/helpers/classCallCheck";
import _createClass from "@babel/runtime/helpers/createClass";
import _assertThisInitialized from "@babel/runtime/helpers/assertThisInitialized";
import _inherits from "@babel/runtime/helpers/inherits";
import _possibleConstructorReturn from "@babel/runtime/helpers/possibleConstructorReturn";
import _getPrototypeOf from "@babel/runtime/helpers/getPrototypeOf";
import _defineProperty from "@babel/runtime/helpers/defineProperty";
function _createSuper(Derived) { var hasNativeReflectConstruct = _isNativeReflectConstruct(); return function _createSuperInternal() { var Super = _getPrototypeOf(Derived), result; if (hasNativeReflectConstruct) { var NewTarget = _getPrototypeOf(this).constructor; result = Reflect.construct(Super, arguments, NewTarget); } else { result = Super.apply(this, arguments); } return _possibleConstructorReturn(this, result); }; }
function _isNativeReflectConstruct() { try { var t = !Boolean.prototype.valueOf.call(Reflect.construct(Boolean, [], function () {})); } catch (t) {} return (_isNativeReflectConstruct = function _isNativeReflectConstruct() { return !!t; })(); }
function ownKeys(e, r) { var t = Object.keys(e); if (Object.getOwnPropertySymbols) { var o = Object.getOwnPropertySymbols(e); r && (o = o.filter(function (r) { return Object.getOwnPropertyDescriptor(e, r).enumerable; })), t.push.apply(t, o); } return t; }
function _objectSpread(e) { for (var r = 1; r < arguments.length; r++) { var t = null != arguments[r] ? arguments[r] : {}; r % 2 ? ownKeys(Object(t), !0).forEach(function (r) { _defineProperty(e, r, t[r]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(e, Object.getOwnPropertyDescriptors(t)) : ownKeys(Object(t)).forEach(function (r) { Object.defineProperty(e, r, Object.getOwnPropertyDescriptor(t, r)); }); } return e; }
/**
 * @jsxRuntime classic
 * @jsx jsx
 */
import React from 'react';

// eslint-disable-next-line @atlaskit/ui-styling-standard/use-compiled -- Ignored via go/DSP-18766
import { css, jsx } from '@emotion/react';
import { format, isValid, parseISO } from 'date-fns';
import pick from 'lodash/pick';
import { createAndFireEvent, withAnalyticsContext, withAnalyticsEvents } from '@atlaskit/analytics-next';
import SelectClearIcon from '@atlaskit/icon/glyph/select-clear';
import { mergeStyles } from '@atlaskit/select';
import { N500, N70 } from '@atlaskit/theme/colors';
import { defaultTimes, formatDateTimeZoneIntoIso } from '../internal';
import { DateTimePickerContainer } from '../internal/date-time-picker-container';
import { convertTokens } from '../internal/parse-tokens';
import DatePicker from './date-picker';
import TimePicker from './time-picker';
var packageName = "@atlaskit/datetime-picker";
var packageVersion = "13.11.0";
// Make DatePicker 50% the width of DateTimePicker
// If rendering an icon container, shrink the TimePicker
var datePickerContainerStyles = css({
  flexBasis: '50%',
  flexGrow: 1,
  flexShrink: 0
});
var timePickerContainerStyles = css({
  flexBasis: '50%',
  flexGrow: 1
});
var iconContainerStyles = css({
  display: 'flex',
  margin: "var(--ds-border-width, 1px)",
  alignItems: 'center',
  flexBasis: 'inherit',
  backgroundColor: 'inherit',
  border: 'none',
  color: "var(--ds-text-subtlest, ".concat(N70, ")"),
  paddingBlockEnd: "var(--ds-space-075, 6px)",
  paddingBlockStart: "var(--ds-space-075, 6px)",
  paddingInlineEnd: "var(--ds-space-100, 8px)",
  paddingInlineStart: "var(--ds-space-050, 4px)",
  transition: "color 150ms",
  '&:hover': {
    color: "var(--ds-text-subtle, ".concat(N500, ")")
  }
});

// react-select overrides (via @atlaskit/select).
var styles = {
  control: function control(style) {
    return _objectSpread(_objectSpread({}, style), {}, {
      backgroundColor: 'transparent',
      border: 2,
      borderRadius: 0,
      paddingLeft: 0,
      ':hover': {
        backgroundColor: 'transparent',
        cursor: 'inherit'
      }
    });
  }
};
var dateTimePickerDefaultProps = {
  appearance: 'default',
  autoFocus: false,
  isDisabled: false,
  name: '',
  // These disables are here for proper typing when used as defaults. They
  // should *not* use the `noop` function.
  /* eslint-disable @repo/internal/react/use-noop */
  onBlur: function onBlur(_event) {},
  onChange: function onChange(_value) {},
  onFocus: function onFocus(_event) {},
  /* eslint-enable @repo/internal/react/use-noop */
  innerProps: {},
  id: '',
  defaultValue: '',
  timeIsEditable: false,
  isInvalid: false,
  datePickerProps: {},
  timePickerProps: {},
  datePickerSelectProps: {},
  timePickerSelectProps: {},
  times: defaultTimes,
  spacing: 'default',
  locale: 'en-US'
  // Not including a default prop for value as it will
  // Make the component a controlled component
};
export var datePickerDefaultAriaLabel = 'Date';
export var timePickerDefaultAriaLabel = 'Time';
var DateTimePickerComponent = /*#__PURE__*/function (_React$Component) {
  _inherits(DateTimePickerComponent, _React$Component);
  var _super = _createSuper(DateTimePickerComponent);
  function DateTimePickerComponent() {
    var _this;
    _classCallCheck(this, DateTimePickerComponent);
    for (var _len = arguments.length, args = new Array(_len), _key = 0; _key < _len; _key++) {
      args[_key] = arguments[_key];
    }
    _this = _super.call.apply(_super, [this].concat(args));
    _defineProperty(_assertThisInitialized(_this), "state", {
      dateValue: '',
      isFocused: false,
      timeValue: '',
      value: _this.props.defaultValue,
      zoneValue: ''
    });
    // All state needs to be accessed via this function so that the state is mapped from props
    // correctly to allow controlled/uncontrolled usage.
    _defineProperty(_assertThisInitialized(_this), "getSafeState", function () {
      var mappedState = _objectSpread(_objectSpread({}, _this.state), pick(_this.props, ['value']));
      return _objectSpread(_objectSpread({}, mappedState), _this.parseValue(mappedState.value, mappedState.dateValue, mappedState.timeValue, mappedState.zoneValue));
    });
    _defineProperty(_assertThisInitialized(_this), "onBlur", function (event) {
      _this.setState({
        isFocused: false
      });
      _this.props.onBlur(event);
    });
    _defineProperty(_assertThisInitialized(_this), "onFocus", function (event) {
      _this.setState({
        isFocused: true
      });
      _this.props.onFocus(event);
    });
    _defineProperty(_assertThisInitialized(_this), "onDateChange", function (dateValue) {
      _this.onValueChange(_objectSpread(_objectSpread({}, _this.getSafeState()), {}, {
        dateValue: dateValue
      }));
    });
    _defineProperty(_assertThisInitialized(_this), "onTimeChange", function (timeValue) {
      _this.onValueChange(_objectSpread(_objectSpread({}, _this.getSafeState()), {}, {
        timeValue: timeValue
      }));
    });
    _defineProperty(_assertThisInitialized(_this), "onClear", function () {
      _this.onValueChange(_objectSpread(_objectSpread({}, _this.getSafeState()), {}, {
        timeValue: '',
        dateValue: ''
      }));
    });
    return _this;
  }
  _createClass(DateTimePickerComponent, [{
    key: "parseValue",
    value: function parseValue(value, dateValue, timeValue, zoneValue) {
      if (this.props.parseValue) {
        return this.props.parseValue(value, dateValue, timeValue, zoneValue);
      }
      var parsed = parseISO(value);
      return isValid(parsed) ? {
        dateValue: format(parsed, convertTokens('YYYY-MM-DD')),
        timeValue: format(parsed, convertTokens('HH:mm')),
        zoneValue: format(parsed, convertTokens('ZZ'))
      } : {
        dateValue: dateValue,
        timeValue: timeValue,
        zoneValue: zoneValue
      };
    }
  }, {
    key: "onValueChange",
    value: function onValueChange(_ref) {
      var dateValue = _ref.dateValue,
        timeValue = _ref.timeValue,
        zoneValue = _ref.zoneValue;
      this.setState({
        dateValue: dateValue,
        timeValue: timeValue,
        zoneValue: zoneValue
      });
      if (dateValue && timeValue) {
        var value = formatDateTimeZoneIntoIso(dateValue, timeValue, zoneValue);
        var _this$parseValue = this.parseValue(value, dateValue, timeValue, zoneValue),
          parsedZone = _this$parseValue.zoneValue;
        var valueWithValidZone = formatDateTimeZoneIntoIso(dateValue, timeValue, parsedZone);
        this.setState({
          value: valueWithValidZone
        });
        this.props.onChange(valueWithValidZone);
        // If the date or time value was cleared when there is an existing datetime value, then clear the value.
      } else if (this.getSafeState().value) {
        this.setState({
          value: ''
        });
        this.props.onChange('');
      }
    }
  }, {
    key: "render",
    value: function render() {
      var _this$props = this.props,
        ariaDescribedBy = _this$props['aria-describedby'],
        appearance = _this$props.appearance,
        autoFocus = _this$props.autoFocus,
        dateFormat = _this$props.dateFormat,
        datePickerProps = _this$props.datePickerProps,
        datePickerSelectProps = _this$props.datePickerSelectProps,
        id = _this$props.id,
        innerProps = _this$props.innerProps,
        isDisabled = _this$props.isDisabled,
        isInvalid = _this$props.isInvalid,
        locale = _this$props.locale,
        name = _this$props.name,
        spacing = _this$props.spacing,
        testId = _this$props.testId,
        timeFormat = _this$props.timeFormat,
        timeIsEditable = _this$props.timeIsEditable,
        timePickerProps = _this$props.timePickerProps,
        timePickerSelectProps = _this$props.timePickerSelectProps,
        times = _this$props.times;
      var _this$getSafeState = this.getSafeState(),
        isFocused = _this$getSafeState.isFocused,
        value = _this$getSafeState.value,
        dateValue = _this$getSafeState.dateValue,
        timeValue = _this$getSafeState.timeValue;
      var _datePickerSelectProp = datePickerSelectProps.styles,
        datePickerStyles = _datePickerSelectProp === void 0 ? {} : _datePickerSelectProp;
      var _timePickerSelectProp = timePickerSelectProps.styles,
        timePickerStyles = _timePickerSelectProp === void 0 ? {} : _timePickerSelectProp;
      var mergedDatePickerSelectProps = _objectSpread(_objectSpread({}, datePickerSelectProps), {}, {
        'aria-describedby': datePickerProps['aria-describedby'] || ariaDescribedBy,
        'aria-label': datePickerProps['label'] || datePickerSelectProps['aria-label'] || datePickerDefaultAriaLabel,
        styles: mergeStyles(styles, datePickerStyles)
      });
      var mergedTimePickerSelectProps = _objectSpread(_objectSpread({}, timePickerSelectProps), {}, {
        'aria-describedby': timePickerProps['aria-describedby'] || ariaDescribedBy,
        'aria-label': timePickerProps['label'] || timePickerSelectProps['aria-label'] || timePickerDefaultAriaLabel,
        styles: mergeStyles(styles, timePickerStyles)
      });

      // Render DateTimePicker's IconContainer when a value has been filled
      // Don't use Date or TimePicker's because they can't be customised
      var isClearable = Boolean(dateValue || timeValue);
      return jsx(DateTimePickerContainer, {
        appearance: appearance,
        isDisabled: isDisabled,
        isFocused: isFocused,
        isInvalid: isInvalid,
        testId: testId,
        innerProps: innerProps
      }, jsx("input", {
        name: name,
        type: "hidden",
        value: value,
        "data-testid": testId && "".concat(testId, "--input")
      }), jsx("div", {
        css: datePickerContainerStyles
      }, jsx(DatePicker, _extends({
        appearance: appearance,
        autoFocus: autoFocus,
        dateFormat: dateFormat,
        hideIcon: true,
        id: id,
        isDisabled: isDisabled,
        isInvalid: isInvalid,
        locale: locale,
        onBlur: this.onBlur,
        onChange: this.onDateChange,
        onFocus: this.onFocus,
        selectProps: mergedDatePickerSelectProps,
        spacing: spacing,
        testId: testId && "".concat(testId, "--datepicker"),
        value: dateValue
      }, datePickerProps))), jsx("div", {
        css: timePickerContainerStyles
      }, jsx(TimePicker, _extends({
        appearance: appearance,
        hideIcon: true,
        isDisabled: isDisabled,
        isInvalid: isInvalid,
        locale: locale,
        onBlur: this.onBlur,
        onChange: this.onTimeChange,
        onFocus: this.onFocus,
        selectProps: mergedTimePickerSelectProps,
        spacing: spacing,
        testId: testId && "".concat(testId, "--timepicker"),
        timeFormat: timeFormat,
        timeIsEditable: timeIsEditable,
        times: times,
        value: timeValue
      }, timePickerProps))), isClearable && !isDisabled ? jsx("button", {
        css: iconContainerStyles,
        onClick: this.onClear,
        "data-testid": testId && "".concat(testId, "--icon--container"),
        tabIndex: -1,
        type: "button"
      }, jsx(SelectClearIcon, {
        size: "small",
        primaryColor: "inherit",
        label: "clear"
      })) : null);
    }
  }]);
  return DateTimePickerComponent;
}(React.Component);
_defineProperty(DateTimePickerComponent, "defaultProps", dateTimePickerDefaultProps);
export { DateTimePickerComponent as DateTimePickerWithoutAnalytics };

/**
 * __Date time picker__
 *
 * A date time picker allows the user to select an associated date and time.
 *
 * - [Examples](https://atlassian.design/components/datetime-picker/examples)
 * - [Code](https://atlassian.design/components/datetime-picker/code)
 * - [Usage](https://atlassian.design/components/datetime-picker/usage)
 */
var DateTimePicker = withAnalyticsContext({
  componentName: 'dateTimePicker',
  packageName: packageName,
  packageVersion: packageVersion
})(withAnalyticsEvents({
  onChange: createAndFireEvent('atlaskit')({
    action: 'changed',
    actionSubject: 'dateTimePicker',
    attributes: {
      componentName: 'dateTimePicker',
      packageName: packageName,
      packageVersion: packageVersion
    }
  })
})(DateTimePickerComponent));
export default DateTimePicker;